import { Empty } from 'antd';
import { useMemo } from 'react';
import ReactECharts, { AXIS_TEXT, CHART_COLORS } from '../charts/echarts';
import { fmtKc, fmtShare } from '../format';
import styles from '../style.module.scss';

export interface Slice {
  name: string;
  value: number;
}

const MAX_SLICES = 6;

/** Donut chung: chỉ lát > 0; quá 6 lát thì gộp phần còn lại thành "Khác". */
export const Donut = ({
  slices,
  height = 240,
  unit = 'KC',
  emptyText = 'Chưa có Kim cương nào.',
}: {
  slices: Slice[];
  height?: number;
  /** Đơn vị trong tooltip ("KC", "Creator"). */
  unit?: string;
  emptyText?: string;
}) => {
  const data = useMemo(() => {
    const positive = slices.filter((s) => s.value > 0).sort((a, b) => b.value - a.value);
    if (positive.length <= MAX_SLICES) return positive;
    const head = positive.slice(0, MAX_SLICES - 1);
    const rest = positive.slice(MAX_SLICES - 1).reduce((sum, s) => sum + s.value, 0);
    return [...head, { name: 'Khác', value: rest }];
  }, [slices]);

  const total = data.reduce((sum, d) => sum + d.value, 0);
  const option = useMemo(
    () => ({
      animation: false,
      color: CHART_COLORS,
      tooltip: {
        trigger: 'item',
        formatter: (p: any) => `${p.name}<br/>${fmtKc(p.value)} ${unit} · ${fmtShare(p.percent)}`,
      },
      // Tỷ trọng ghi ở chú thích (không vẽ nhãn quanh vòng: màn 390px bị cắt chữ).
      legend: {
        bottom: 0,
        left: 'center',
        type: 'scroll',
        textStyle: { color: AXIS_TEXT },
        formatter: (name: string) => {
          const hit = data.find((d) => d.name === name);
          return hit && total > 0 ? `${name} · ${fmtShare((hit.value / total) * 100)}` : name;
        },
      },
      series: [
        {
          type: 'pie',
          radius: ['48%', '72%'],
          center: ['50%', '42%'],
          avoidLabelOverlap: true,
          itemStyle: { borderColor: '#fff', borderWidth: 2 },
          label: { show: false },
          data,
        },
      ],
    }),
    [data, total, unit]
  );

  if (!data.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />;
  }
  return <ReactECharts option={option} style={{ height, width: '100%' }} />;
};

export interface MixValues {
  pk: number;
  other: number;
  overseas: number | null;
  multiGuest: number | null;
  fanClub: number | null;
}

/**
 * Cơ cấu KC. PK + Ngoài PK là một phép chia trọn tổng KC nên vẽ donut; Quốc tế / Đa khách / Fan club là
 * phần NẰM TRONG tổng (chồng lên PK / ngoài PK — backend: otherDiamonds = tổng − PK) nên KHÔNG ghép vào cùng
 * donut (tỷ trọng sẽ sai), mà hiện "Trong đó" theo % tổng KC.
 */
const MixDonut = ({ mix, title = 'Cơ cấu Kim cương' }: { mix: MixValues; title?: string }) => {
  const total = (mix.pk || 0) + (mix.other || 0);
  const extras: { name: string; value: number | null }[] = [
    { name: 'KC quốc tế', value: mix.overseas },
    { name: 'KC phòng đa khách', value: mix.multiGuest },
    { name: 'KC Fan club', value: mix.fanClub },
  ];
  const shown = extras.filter((e) => e.value !== null && e.value > 0) as {
    name: string;
    value: number;
  }[];

  return (
    <section className={styles.card} aria-label={title}>
      <h2 className={styles.cardTitle}>{title}</h2>
      <Donut
        slices={[
          { name: 'Trận PK', value: mix.pk || 0 },
          { name: 'Ngoài PK', value: mix.other || 0 },
        ]}
      />
      {total > 0 && shown.length > 0 && (
        <ul className={styles.mixExtras} aria-label="Trong tổng Kim cương">
          {shown.map((e) => {
            const share = (e.value / total) * 100;
            return (
              <li key={e.name}>
                <span className={styles.mixLabel}>Trong đó {e.name}</span>
                <span className={styles.mixBar} aria-hidden>
                  <span style={{ width: `${Math.min(100, share)}%` }} />
                </span>
                <span className={styles.mixValue}>
                  {fmtKc(e.value)} · {fmtShare(share)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default MixDonut;
